import React, { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import DetailLayout from "../../shared/DetailLayout";
import UserOverview from "./UserOverview";
import UserRightPanel from "./UserRightPanel";
import type { UserDto } from "../../../api-client";
import { useUserById } from "../../../stores/User/user.queries";
import { UnsavedChangesProvider, useUnsavedChanges } from "./UnsavedChangesGuard";

interface UserLayoutProps {
  /** The selected user object (typically a lightweight row from the user list) */
  user: UserDto;
  /** Callback to close user details and return to the user list */
  onClose: () => void;
  /** Callback when user data is updated (e.g. after save) */
  onSelectUser: (user: UserDto | null) => void;
}

/**
 * Two-panel layout for the User detail view.
 * Left: UserOverview card (read-only summary with close button)
 * Right: UserRightPanel (horizontal tabs: Info, Berechtigung, Fortbildungen, Arbeitsplan, Urlaub)
 *
 * The selected `user` prop coming from the list view only contains lightweight
 * row data (no roles/permissions/etc., since the list endpoint no longer eager-loads
 * those relations). We fetch the full user record here via `useUserById` and pass
 * the merged result to the children. This keeps the heavy user query out of the
 * list endpoint while preserving the existing useQuery wrapper pattern.
 */
const UserLayout: React.FC<UserLayoutProps> = ({ user, onClose, onSelectUser }) => {
  // Only fetch when we have a persisted user (i.e. an id). For new-user flows
  // (no id yet) we just pass the empty draft straight through.
  const userId = user?.id;
  const { data: fetchedUser, isLoading } = useUserById(userId, !!userId);

  // Merge: fetched data takes precedence, falling back to the row data while
  // loading. Memoised because the children re-sync their form state whenever
  // this prop changes — building a fresh object on every render would discard
  // the admin's unsaved edits on any incidental re-render (a MobX tick, or the
  // background refetch `useUserById` performs on window focus once its 5-minute
  // staleTime has elapsed).
  const effectiveUser: UserDto = useMemo(
    () => (fetchedUser ? { ...user, ...fetchedUser } : user),
    [user, fetchedUser]
  );

  if (userId && isLoading && !fetchedUser) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <UnsavedChangesProvider>
      <UserLayoutBody
        user={effectiveUser}
        onClose={onClose}
        onSelectUser={onSelectUser}
      />
    </UnsavedChangesProvider>
  );
};

/**
 * Rendered inside the provider so the close button can consult the guard.
 * Closing the detail view unmounts the form, so without this the admin's
 * unsaved edits would disappear with no warning.
 */
const UserLayoutBody: React.FC<{
  user: UserDto;
  onClose: () => void;
  onSelectUser: (user: UserDto | null) => void;
}> = ({ user, onClose, onSelectUser }) => {
  const { guard } = useUnsavedChanges();

  return (
    <DetailLayout
      leftContent={<UserOverview user={user} onClose={() => guard(onClose)} />}
      rightContent={<UserRightPanel user={user} onSelectUser={onSelectUser} />}
      showRightPanel={true}
      leftWidth={2.6}
    />
  );
};

export default UserLayout;
